/**
 * Runtime Configuration
 *
 * Reads config from window.__CLOUDNEST_CONFIG (injected by Docker entrypoint)
 * with local dev fallbacks. This allows a single Docker image to serve
 * both production and staging with different env vars.
 */

import { type AiBotEntry, parseAiBots } from "./ai-bots";

interface CloudnestConfig {
  readonly authServiceUrl: string;
  readonly multiplayerServiceUrl: string;
  readonly authPathPrefix: string;
  readonly enableRegister: boolean;
  readonly googleOAuthClientId: string;
  /** Base URL of the game AI bot worker; empty hides AI invite controls. */
  readonly aiBotServiceUrl: string;
  /**
   * Per-game AI bot rosters keyed by gameType, parsed and validated from the
   * GAME_AI_BOTS env var. The client renders whatever roster it is handed —
   * adding or removing a champion is a config-only change (see ai-bots.ts).
   */
  readonly aiBots: Readonly<Record<string, readonly AiBotEntry[]>>;
}

declare global {
  interface Window {
    readonly __CLOUDNEST_CONFIG?: {
      readonly AUTH_SERVICE_URL?: string;
      readonly MULTIPLAYER_SERVICE_URL?: string;
      readonly AUTH_PATH_PREFIX?: string;
      readonly ENABLE_REGISTER?: string;
      readonly GOOGLE_OAUTH_CLIENT_ID?: string;
      readonly AI_BOT_SERVICE_URL?: string;
      /** JSON map of gameType → [{ id, label, url }] AI bot rosters. */
      readonly GAME_AI_BOTS?: string;
    };
  }
}

let cachedConfig: CloudnestConfig | null = null;

export function getConfig(): CloudnestConfig {
  if (cachedConfig) {
    return cachedConfig;
  }

  const raw = window.__CLOUDNEST_CONFIG;

  cachedConfig = Object.freeze({
    authServiceUrl:
      raw?.AUTH_SERVICE_URL ?? "https://func-api.wintersalmon.com",
    multiplayerServiceUrl:
      raw?.MULTIPLAYER_SERVICE_URL ?? "https://func-api.wintersalmon.com",
    authPathPrefix: raw?.AUTH_PATH_PREFIX ?? "/api/v1/auth",
    enableRegister: (raw?.ENABLE_REGISTER ?? "TRUE") === "TRUE",
    googleOAuthClientId: raw?.GOOGLE_OAUTH_CLIENT_ID ?? "",
    aiBotServiceUrl: raw?.AI_BOT_SERVICE_URL ?? "",
    aiBots: parseAiBots(raw?.GAME_AI_BOTS),
  });

  return cachedConfig;
}
